import { useState, useRef, useMemo, lazy, Suspense } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Alert, TextField, Button, Grid } from "@mui/material";
import { BasePage, LinkButton, LoadingButton } from "components";
import { useAuthentication, useSnack } from "hooks";
import { userService } from "services";
import { PHONE_VERIFICATION_ERROR_MESSAGES, getErrorMessage } from "constants";

const MuiTelInput = lazy(() =>
 import("mui-tel-input").then((m) => ({ default: m.MuiTelInput })),
);

export function PhoneNumberPage() {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const location = useLocation();
 const {
  user,
  isButtonLoading,
  sendPhoneVerification,
  verifyPhoneNumber,
  validatePhone,
  normalizePhone,
 } = useAuthentication();
 const { openSnack } = useSnack();

 // Check if this is post-signup flow
 const isPostSignup = location.pathname.includes("/sign-up/phone-number");
 const returnUrl =
  location.state?.returnUrl ||
  searchParams.get("return") ||
  (isPostSignup ? "/profile" : "/settings");

 const [phase, setPhase] = useState("phone"); // 'phone' | 'code' | 'done'
 const [phone, setPhone] = useState(user?.phoneNumber || "");
 const [phoneValid, setPhoneValid] = useState(false);
 const [phoneError, setPhoneError] = useState("");
 const [code, setCode] = useState("");
 const [codeError, setCodeError] = useState("");

 const verificationIdRef = useRef(null);

 const codeValid = useMemo(() => /^\d{6}$/.test(code), [code]);

 // Page title based on context
 const pageTitle = isPostSignup ? "Add phone number" : "Update phone number";

 // Handlers
 const onPhoneChange = async (value) => {
  const { matchIsValidTel } = await import("mui-tel-input");
  setPhone(value);
  setPhoneValid(matchIsValidTel(value));

  if (!value) {
   setPhoneError("");
   return;
  }
  const validation = validatePhone(normalizePhone(value));
  if (!validation.valid) {
   setPhoneError(validation.error);
  } else if (!matchIsValidTel(value)) {
   setPhoneError("Invalid phone number.");
  } else {
   setPhoneError("");
  }
 };

 const handleSendCode = async () => {
  // Final validation before sending
  const normalizedPhone = normalizePhone(phone);
  const validation = validatePhone(normalizedPhone);

  if (!phoneValid || !validation.valid) {
   setPhoneError(validation.error || "Invalid phone number.");
   return;
  }

  try {
   const result = await sendPhoneVerification(
    normalizedPhone,
    "phone-recaptcha-container",
   );
   verificationIdRef.current = result.verificationId;
   setPhase("code");
   setCode("");
   setCodeError("");
  } catch (error) {
   // Use consistent error messaging
   const errorMessage = getErrorMessage(
    error,
    PHONE_VERIFICATION_ERROR_MESSAGES,
   );
   setPhoneError(errorMessage);
  }
 };

 const handleVerify = async () => {
  if (!codeValid) {
   setCodeError("Enter the 6-digit code.");
   return;
  }

  if (!verificationIdRef.current) {
   setCodeError("Request a code first.");
   return;
  }

  try {
   await verifyPhoneNumber(verificationIdRef.current, code);

   // Update user document in Firestore (only if user ID available)
   if (user?.uid) {
    try {
     await userService.updateUserDoc(user.uid, {
      phoneNumber: normalizePhone(phone),
     });
    } catch (error) {
     console.error("Failed to update phone in Firestore:", error);
     // Continue anyway since Firebase auth was successful
    }
   }

   setPhase("done");
   openSnack("Phone number updated successfully", "success");

   // Auto-redirect after success
   setTimeout(() => {
    navigate(returnUrl);
   }, 2000);
  } catch (error) {
   if (error.code === "auth/code-expired") {
    setPhase("phone");
    setCode("");
    verificationIdRef.current = null;
   }
   // Use consistent error messaging
   const errorMessage = getErrorMessage(
    error,
    PHONE_VERIFICATION_ERROR_MESSAGES,
   );
   setCodeError(errorMessage);
  }
 };

 const handleSkip = () => {
  navigate(returnUrl);
 };

 const handleBackToPhone = () => {
  setPhase("phone");
  setCode("");
  setCodeError("");
  verificationIdRef.current = null;
 };

 // Enter to submit
 const onPhoneKeyDown = (e) => {
  if (
   e.key === "Enter" &&
   !isButtonLoading &&
   phoneValid &&
   phase === "phone"
  ) {
   handleSendCode();
  }
 };

 const onCodeKeyDown = (e) => {
  if (e.key === "Enter" && !isButtonLoading && codeValid && phase === "code") {
   handleVerify();
  }
 };

 return (
  <BasePage title={pageTitle}>
   <Grid container spacing={3}>
    {/* Email field */}
    <Grid size={12}>
     <TextField fullWidth label="Email" value={user?.email || ""} disabled />
    </Grid>

    {/* Phone Number field */}
    <Grid size={12}>
     <Suspense fallback={<TextField fullWidth label="Phone Number" disabled />}>
      <MuiTelInput
       label="Phone Number"
       defaultCountry="ZA"
       fullWidth
       value={phone}
       onChange={onPhoneChange}
       onKeyDown={onPhoneKeyDown}
       forceCallingCode
       disabled={phase !== "phone"}
       error={!!phoneError}
       helperText={phoneError}
      />
     </Suspense>
    </Grid>

    {/* Verification Code field - only show when needed */}
    {phase !== "phone" && (
     <Grid size={12}>
      <TextField
       fullWidth
       label="Verification Code"
       placeholder="123456"
       value={code}
       onChange={(e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 6);
        setCode(v);
        setCodeError(
         v.length && !/^\d{6}$/.test(v) ? "6 digits required." : "",
        );
       }}
       onKeyDown={onCodeKeyDown}
       disabled={phase !== "code"}
       error={!!codeError}
       helperText={codeError}
       slotProps={{
        input: {
         inputMode: "numeric",
         pattern: "[0-9]*",
         maxLength: 6,
        },
       }}
      />
     </Grid>
    )}

    {/* Info/Success alert */}
    {phase === "code" && (
     <Grid size={12}>
      <Alert severity="info">
       We've sent a 6-digit verification code to <strong>{phone}</strong>
      </Alert>
     </Grid>
    )}
    {phase === "done" && (
     <Grid size={12}>
      <Alert severity="success">
       <strong>{normalizePhone(phone)}</strong> has been successfully updated.
       Redirecting you back...
      </Alert>
     </Grid>
    )}

    {/* Main action button */}
    <Grid size={12}>
     {phase === "phone" ? (
      <LoadingButton
       fullWidth
       variant="contained"
       onClick={handleSendCode}
       loading={isButtonLoading}
       disabled={!phoneValid}
      >
       Send Verification Code
      </LoadingButton>
     ) : phase === "code" ? (
      <LoadingButton
       fullWidth
       variant="contained"
       onClick={handleVerify}
       loading={isButtonLoading}
       disabled={!codeValid}
      >
       Verify Phone Number
      </LoadingButton>
     ) : (
      <LoadingButton fullWidth variant="contained" disabled>
       Redirecting...
      </LoadingButton>
     )}
    </Grid>

    {/* Secondary actions */}
    <Grid size={12} className="form-actions">
     {phase === "phone" ? (
      isPostSignup ? (
       <LinkButton onClick={handleSkip}>Skip for now</LinkButton>
      ) : (
       <LinkButton to="/settings">Back to settings</LinkButton>
      )
     ) : phase === "code" ? (
      <>
       <Button
        variant="text"
        onClick={handleBackToPhone}
        disabled={isButtonLoading}
       >
        Use different number
       </Button>
       {isPostSignup ? (
        <LinkButton onClick={handleSkip}>Skip for now</LinkButton>
       ) : (
        <LinkButton to="/settings">Cancel</LinkButton>
       )}
      </>
     ) : null}
    </Grid>

    {/* Hidden reCAPTCHA container */}
    <div id="phone-recaptcha-container" style={{ display: "none" }} />
   </Grid>
  </BasePage>
 );
}
